/**
 * Advanced filters: several artists at once, and min/max ranges for BPM, energy
 * level and danceability.
 *
 * Sits under the search box and folds away. Everything here becomes clauses in
 * the same query the box produces (see ``filters.ts``), so the typed terms and
 * the panel narrow one result set together, filtered by the engine across the
 * whole library.
 */

import { useEffect, useState } from "react";
import { fetchArtists } from "../api";
import { BOUNDS, EMPTY_FILTERS, isActive, type Filters } from "./filters";

/** Two thumbs on one track. Each thumb is a real range input, so keyboards work. */
export function RangeSlider({
  label,
  bounds,
  step,
  value,
  format,
  onChange,
}: {
  label: string;
  bounds: [number, number];
  step: number;
  value: [number, number] | null;
  format: (v: number) => string;
  onChange: (v: [number, number] | null) => void;
}) {
  const [lo, hi] = value ?? bounds;
  const span = bounds[1] - bounds[0];
  const left = ((lo - bounds[0]) / span) * 100;
  const right = ((hi - bounds[0]) / span) * 100;
  const active = value !== null && (lo > bounds[0] || hi < bounds[1]);

  const set = (next: [number, number]) => {
    const clean: [number, number] = [Math.min(next[0], next[1]), Math.max(next[0], next[1])];
    onChange(clean[0] <= bounds[0] && clean[1] >= bounds[1] ? null : clean);
  };

  return (
    <div className={`range ${active ? "active" : ""}`}>
      <div className="range-head">
        <span className="range-label">{label}</span>
        <span className="range-value">
          {active ? `${format(lo)} – ${format(hi)}` : "any"}
        </span>
        {active && (
          <button className="range-reset" onClick={() => onChange(null)} title="Clear this range">
            ×
          </button>
        )}
      </div>
      <div className="range-track">
        <div className="range-fill" style={{ left: `${left}%`, width: `${Math.max(0, right - left)}%` }} />
        <input
          type="range"
          min={bounds[0]}
          max={bounds[1]}
          step={step}
          value={lo}
          aria-label={`${label} minimum`}
          onChange={(e) => set([Math.min(Number(e.target.value), hi), hi])}
        />
        <input
          type="range"
          min={bounds[0]}
          max={bounds[1]}
          step={step}
          value={hi}
          aria-label={`${label} maximum`}
          onChange={(e) => set([lo, Math.max(Number(e.target.value), lo)])}
        />
      </div>
    </div>
  );
}

function ArtistPicker({
  selected,
  offline,
  onChange,
}: {
  selected: string[];
  offline: boolean;
  onChange: (next: string[]) => void;
}) {
  const [text, setText] = useState("");
  const [options, setOptions] = useState<{ name: string; tracks: number }[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (offline || !open) return;
    const timer = setTimeout(() => {
      void fetchArtists(text, 30).then((list) => setOptions(list));
    }, 150);
    return () => clearTimeout(timer);
  }, [text, open, offline]);

  const add = (name: string) => {
    const clean = name.trim();
    if (!clean || selected.some((s) => s.toLowerCase() === clean.toLowerCase())) return;
    onChange([...selected, clean]);
    setText("");
  };

  const chosen = new Set(selected.map((s) => s.toLowerCase()));

  return (
    <div className="artist-picker">
      <div className="range-head">
        <span className="range-label">Artists</span>
        <span className="range-value">{selected.length ? `any of ${selected.length}` : "any"}</span>
      </div>
      <div className="artist-chips">
        {selected.map((name) => (
          <span key={name} className="artist-chip">
            {name}
            <button onClick={() => onChange(selected.filter((s) => s !== name))} title={`Remove ${name}`}>
              ×
            </button>
          </span>
        ))}
        <input
          value={text}
          placeholder={selected.length ? "add another…" : "type an artist…"}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add(options.find((o) => !chosen.has(o.name.toLowerCase()))?.name ?? text);
            }
            if (e.key === "Backspace" && !text && selected.length) {
              onChange(selected.slice(0, -1));
            }
          }}
        />
      </div>
      {open && options.length > 0 && (
        <ul className="artist-options">
          {options
            .filter((o) => !chosen.has(o.name.toLowerCase()))
            .slice(0, 12)
            .map((o) => (
              <li key={o.name}>
                <button onMouseDown={(e) => e.preventDefault()} onClick={() => add(o.name)}>
                  {o.name} <em>{o.tracks}</em>
                </button>
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}

export function FilterPanel({
  filters,
  offline,
  onChange,
}: {
  filters: Filters;
  offline: boolean;
  onChange: (next: Filters) => void;
}) {
  return (
    <div className="filter-panel">
      <ArtistPicker
        selected={filters.artists}
        offline={offline}
        onChange={(artists) => onChange({ ...filters, artists })}
      />
      <RangeSlider
        label="BPM"
        bounds={BOUNDS.bpm}
        step={1}
        value={filters.bpm}
        format={(v) => String(Math.round(v))}
        onChange={(bpm) => onChange({ ...filters, bpm })}
      />
      <RangeSlider
        label="Energy level"
        bounds={BOUNDS.energyLevel}
        step={1}
        value={filters.energyLevel}
        format={(v) => String(v)}
        onChange={(energyLevel) => onChange({ ...filters, energyLevel })}
      />
      <RangeSlider
        label="Danceability"
        bounds={BOUNDS.danceability}
        step={0.05}
        value={filters.danceability}
        format={(v) => v.toFixed(2)}
        onChange={(danceability) => onChange({ ...filters, danceability })}
      />
      <RangeSlider
        label="LUFS"
        bounds={BOUNDS.lufs}
        step={0.5}
        value={filters.lufs ?? null}
        format={(v) => v.toFixed(1)}
        onChange={(lufs) => onChange({ ...filters, lufs })}
      />
      <button
        className="ghost filter-clear"
        disabled={!isActive(filters)}
        onClick={() => onChange(EMPTY_FILTERS)}
      >
        Clear filters
      </button>
    </div>
  );
}
